import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { apiService } from "../../services/api.ts";
import "./ScheduleCard.css";
import Reveal from "../motion/Reveal";

type CalEvent = {
  id: string;
  title: string;
  start: string | Date;
  end: string | Date;
  [key: string]: any;
};

interface ScheduleCardProps {
  /** Koľko položiek max. zobraziť (default 5) */
  maxItems?: number;
  /** Ak už máš udalosti z parenta (napr. z CalendarPage), môžeš ich sem poslať a nebudeme fetchovať. */
  eventsFromProps?: CalEvent[];
  /** Optional dashboard reveal; omitted in utility panels that have no motion provider. */
  motionPreset?: "dashboard";
  motionDelay?: number;
}

const colorCycle = ["dot-blue", "dot-red", "dot-green"];

const EMPTY_DRAFT = {
  title: "",
  start: "",
  end: "",
};

const pad = (value: number) => String(value).padStart(2, "0");

const toLocalDateTimeInput = (value: Date) => [
  value.getFullYear(),
  "-",
  pad(value.getMonth() + 1),
  "-",
  pad(value.getDate()),
  "T",
  pad(value.getHours()),
  ":",
  pad(value.getMinutes()),
].join("");

const getDefaultEventRange = () => {
  const start = new Date();
  start.setSeconds(0, 0);
  start.setMinutes(Math.ceil(start.getMinutes() / 30) * 30);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { start, end };
};

/** Jednoduché “hašovanie” názvu -> farba bodky, aby mali položky konzistentnú farbu naprieč rendermi. */
function colorClassFor(title: string) {
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
  }
  return colorCycle[hash % colorCycle.length];
}

export default function ScheduleCard({ maxItems = 5, eventsFromProps, motionPreset, motionDelay = 0 }: ScheduleCardProps) {
  const { t } = useTranslation();
  const { token, userId } = useAuth();
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  // Ak neprídu events z parenta, načítame ich z API
  useEffect(() => {
    if (eventsFromProps && eventsFromProps.length) {
      setEvents(normalize(eventsFromProps));
      return;
    }
    if (!token || !userId) return;

    setLoading(true);
    apiService
      .getCalendarEvents(token, userId)
      .then((data: CalEvent[]) => setEvents(normalize(data)))
      .catch((err: any) => {
        console.error("Calendar fetch error:", err);
        toast.error(t("calendar_fetch_error", { defaultValue: "Error fetching calendar events" }), {
          position: "bottom-left",
        });
      })
      .finally(() => setLoading(false));
  }, [token, userId, eventsFromProps, t]);

  // Udalosti len na dnešok (podľa lokálneho času), zoradené podľa začiatku, budúce + práve prebiehajúce
  const todayItems = useMemo(() => {
    const startOfDay = moment().startOf("day");
    const endOfDay = moment().endOf("day");
    const now = moment();

    return events
      .filter((e) => {
        const s = moment(e.start);
        const eEnd = moment(e.end);
        // patrí do dnešného dňa a (už prebieha alebo ešte nezačala)
        return (
          s.isSameOrAfter(startOfDay) &&
          s.isSameOrBefore(endOfDay) &&
          eEnd.isAfter(now)
        );
      })
      .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf())
      .slice(0, maxItems);
  }, [events, maxItems]);

  const openEditor = () => {
    const range = getDefaultEventRange();
    setDraft({
      title: "",
      start: toLocalDateTimeInput(range.start),
      end: toLocalDateTimeInput(range.end),
    });
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    if (isSaving) return;
    setIsEditorOpen(false);
    setDraft(EMPTY_DRAFT);
  };

  const handleSave = async (submitEvent: React.FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();

    const title = draft.title.trim();
    const start = new Date(draft.start);
    const end = new Date(draft.end);

    if (!title) {
      toast.warning(t("calendar.title_required", { defaultValue: "Enter an event title." }), {
        position: "bottom-left",
      });
      return;
    }

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      toast.warning(t("calendar.invalid_range", { defaultValue: "The end must be after the start." }), {
        position: "bottom-left",
      });
      return;
    }

    if (!token || !userId) return;

    setIsSaving(true);
    try {
      const payload = {
        title,
        start: start.toISOString(),
        end: end.toISOString(),
        userId,
      };
      const data = await apiService.createCalendarEvent(token, payload);
      const [created] = normalize([{ ...payload, ...data, id: data?.id || data?.event_id }]);

      if (!created) throw new Error("Calendar API returned an invalid event");

      setEvents((currentEvents) => [...currentEvents, created]);
      setIsEditorOpen(false);
      setDraft(EMPTY_DRAFT);
      toast.success(t("calendar.saved", { defaultValue: "Event saved." }), {
        position: "bottom-left",
      });
    } catch (error) {
      console.error("Error saving calendar event:", error);
      toast.error(t("calendar.save_error", { defaultValue: "Event could not be saved." }), {
        position: "bottom-left",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const Wrapper = motionPreset ? Reveal : "div";
  const wrapperProps = {
    className: "dash-card dashboard-schedule-card text-start p-3 mb-3",
    ...(motionPreset ? { preset: motionPreset, delay: motionDelay } : {}),
  };

  return (
    <>
      <Wrapper {...wrapperProps}>
        <div className="schedule-card-heading">
          <div>
            <h5 className="mb-1">{t("schedule.today", { defaultValue: "Today's Schedule" })}</h5>
            <div className="text-muted small">
              {t("schedule.subtitle", { defaultValue: "Upcoming tasks and meetings" })}
            </div>
          </div>
          <button type="button" className="schedule-add-event-button" onClick={openEditor}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            <span>{t("add_event")}</span>
          </button>
        </div>

        {/* Loading stav */}
        {loading && (
          <>
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
          </>
        )}

        {/* Empty stav */}
        {!loading && todayItems.length === 0 && (
          <div className="schedule-empty-state text-muted small">
            {t("schedule.empty_today", { defaultValue: "No events for today." })}
          </div>
        )}

        {/* Reálne dáta */}
        {!loading &&
          todayItems.map((ev) => {
            const startLabel = moment(ev.start).format("LT");
            const endLabel = moment(ev.end).format("LT");
            const dotClass = colorClassFor(ev.title || "");
            return (
              <div className="sched-item" key={ev.id}>
                <span className={`dot ${dotClass}`} />
                <div>
                  <div className="fw-semibold text-truncate" title={ev.title}>
                    {ev.title}
                  </div>
                  <div className="small text-muted">
                    {startLabel} – {endLabel}
                  </div>
                </div>
              </div>
            );
          })}
      </Wrapper>

      <Modal show={isEditorOpen} onHide={closeEditor} centered className="schedule-event-modal">
        <Modal.Header closeButton>
          <div>
            <div className="schedule-modal-eyebrow">
              {t("calendar.new_event", { defaultValue: "New event" })}
            </div>
            <Modal.Title>{t("calendar.event_details", { defaultValue: "Event details" })}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form id="schedule-event-form" onSubmit={handleSave}>
            <Form.Group className="mb-3" controlId="scheduleEventTitle">
              <Form.Label>{t("event_title")}</Form.Label>
              <Form.Control
                autoFocus
                required
                maxLength={255}
                type="text"
                placeholder={t("enter_event_title")}
                value={draft.title}
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
              />
            </Form.Group>
            <div className="schedule-date-grid">
              <Form.Group controlId="scheduleEventStart">
                <Form.Label>{t("start_time")}</Form.Label>
                <Form.Control
                  required
                  type="datetime-local"
                  step="900"
                  value={draft.start}
                  onChange={(event) => setDraft((current) => ({ ...current, start: event.target.value }))}
                />
              </Form.Group>
              <Form.Group controlId="scheduleEventEnd">
                <Form.Label>{t("end_time")}</Form.Label>
                <Form.Control
                  required
                  type="datetime-local"
                  step="900"
                  min={draft.start}
                  value={draft.end}
                  onChange={(event) => setDraft((current) => ({ ...current, end: event.target.value }))}
                />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeEditor} disabled={isSaving}>
            {t("calendar.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button type="submit" form="schedule-event-form" className="schedule-save-button" disabled={isSaving}>
            {isSaving && <Spinner animation="border" size="sm" className="me-2" />}
            {t("save_changes")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/** Pomocná normalizácia: validné dátumy, konverzia na Date a istota id/title */
function normalize(data: CalEvent[]): CalEvent[] {
  return (data || [])
    .filter(
      (e) =>
        e &&
        e.id &&
        e.title &&
        e.start &&
        e.end &&
        !isNaN(new Date(e.start as any).valueOf()) &&
        !isNaN(new Date(e.end as any).valueOf())
    )
    .map((e) => ({
      ...e,
      start: new Date(e.start as any),
      end: new Date(e.end as any),
    }));
}

function SkeletonItem() {
  return (
    <div className="sched-item">
      <span className="dot dot-blue" />
      <div className="w-100">
        <div className="placeholder-wave">
          <span className="placeholder col-7" />
        </div>
        <div className="placeholder-wave mt-1">
          <span className="placeholder col-4" />
        </div>
      </div>
    </div>
  );
}
